/**
 * The call panel's Ask tab (call-center B3): the staff member types what the caller asked, and the assistant answers in a
 * second or two with a short script to read out, from this event's details and the answers its managers have written.
 * When it isn't sure, a badge says to check before saying it. Send to admin saves the question, with the answer shown, for
 * the event's Caller questions page; a question it couldn't answer can be sent without one. Every question asked on this
 * call stays listed under the newest while the panel is open.
 */
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { TextArea } from '@/components/ui/Field';
import { INFO_TARGET_LEFT, InfoTip } from '@/components/ui/InfoTip';
import { InlineReport } from '@/components/ui/ReportControl';
import { cn } from '@/lib/cn';
import { ASK_FAILED, SEND_FAILED, askAssistant, sendQuestion } from './data';
import { QUESTION_MAX, type AssistAnswer } from './questions';

/** One question on this call: what came back (null = sent without an answer), and whether it went to the managers. */
interface Turn { id: number; question: string; answer: AssistAnswer | null; sent: 'no' | 'sending' | 'sent'; error: string | null }

export function AskTab({ callId, eventId }: { callId: string; eventId: string | null }) {
  const [text, setText] = useState('');
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // the question the error is about, which can still go to the managers without an answer
  const [failed, setFailed] = useState<string | null>(null);
  const [sendingFailed, setSendingFailed] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);

  const patch = (id: number, p: Partial<Turn>) => setTurns((all) => all.map((t) => (t.id === id ? { ...t, ...p } : t)));

  async function ask() {
    const question = text.trim();
    if (!question || asking || !eventId) return;
    setAsking(true); setError(null); setFailed(null);
    const r = await askAssistant(eventId, question);
    setAsking(false);
    if (!r.ok || !r.data || typeof r.data.answer !== 'string') { setError(r.ok ? ASK_FAILED : r.message || ASK_FAILED); setFailed(question); return; }
    const answer = { answer: r.data.answer, confident: r.data.confident === true };
    setTurns((all) => [{ id: Date.now(), question, answer, sent: 'no', error: null }, ...all]);
    // the box stays open while the assistant answers; what was typed meanwhile is kept
    setText((now) => (now.trim() === question ? '' : now));
  }

  async function send(t: Turn) {
    if (!eventId || t.sent !== 'no') return;
    patch(t.id, { sent: 'sending', error: null });
    const r = await sendQuestion({ event_id: eventId, call_id: callId, question: t.question, ...(t.answer ? { ai_answer: t.answer.answer } : {}) });
    patch(t.id, r.ok ? { sent: 'sent' } : { sent: 'no', error: r.message || SEND_FAILED });
  }

  /** The question the assistant couldn't answer, sent on its own; it then joins this call's list as sent. */
  async function sendFailed() {
    const question = failed;
    if (!eventId || !question || sendingFailed) return;
    setSendingFailed(true);
    const r = await sendQuestion({ event_id: eventId, call_id: callId, question });
    setSendingFailed(false);
    if (!r.ok) { setError(r.message || SEND_FAILED); return; }
    setTurns((all) => [{ id: Date.now(), question, answer: null, sent: 'sent', error: null }, ...all]);
    setError(null); setFailed(null);
    if (text.trim() === question) setText('');
  }

  const [latest, ...earlier] = turns;
  return (
    <section aria-label="Ask about this event" className="flex flex-col gap-4" data-testid="call-ask">
      <form className="flex flex-col gap-2" onSubmit={(e) => { e.preventDefault(); void ask(); }}>
        <div className="relative pr-8">
          <TextArea label="What did the caller ask?" value={text} maxLength={QUESTION_MAX} rows={3}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); void ask(); } }}
            data-testid="ask-input" />
          <InfoTip title="Ask" className={cn('absolute right-0 top-0', INFO_TARGET_LEFT)} align="end">
            Type the caller’s question and press Enter. The assistant answers from this event’s details and the answers its managers have written, in words you can read out. When it isn’t sure, check before you say it. Send to admin saves the question for the event’s managers; once they answer it, the assistant uses their answer.
          </InfoTip>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="submit" variant="primary" className="max-sm:min-h-11" busy={asking} busyLabel="Asking…" disabled={!text.trim() || !eventId} data-testid="ask-submit">Ask</Button>
          <span className="ml-auto text-[12px] tabular-nums text-muted" aria-live="polite">{text.length}/{QUESTION_MAX}</span>
        </div>
        {error && (
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <p role="alert" className="text-st-danger" data-testid="ask-error">{error}</p>
            <InlineReport message={error} />
            {failed && (
              <Button className="max-sm:min-h-11" busy={sendingFailed} busyLabel="Sending…" onClick={() => void sendFailed()} data-testid="ask-send-unanswered">Send to admin</Button>
            )}
          </div>
        )}
      </form>
      {latest && <TurnCard t={latest} latest onSend={() => void send(latest)} />}
      {earlier.length > 0 && (
        <section aria-label="Earlier on this call" className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold text-ink">Earlier on this call</h3>
          <ul className="flex flex-col gap-2" data-testid="ask-earlier">
            {earlier.map((t) => <li key={t.id}><TurnCard t={t} onSend={() => void send(t)} /></li>)}
          </ul>
        </section>
      )}
    </section>
  );
}

function TurnCard({ t, latest = false, onSend }: { t: Turn; latest?: boolean; onSend(): void }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const h = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(h);
  }, [copied]);
  // the test ids name the newest answer only, so each is one element
  const id = (s: string) => (latest ? s : undefined);
  const copy = () => void navigator.clipboard.writeText(t.answer?.answer ?? '').then(() => setCopied(true), () => undefined);
  return (
    <article className={cn('flex flex-col gap-2 rounded-card border border-line p-3 text-sm', !latest && 'bg-surface-2/40')} data-testid={latest ? 'ask-turn' : 'ask-turn-earlier'}>
      <p className="whitespace-pre-wrap break-words text-ink-2">{t.question}</p>
      {t.answer ? (
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-[12px] font-semibold text-ink-2">Say:</p>
            {!t.answer.confident && <Badge tone="warning" data-testid={id('ask-unsure')}>Not sure — check before saying it</Badge>}
          </div>
          <p className={cn('whitespace-pre-line break-words text-ink', latest && 'text-[15px]')} data-testid={id('ask-answer')}>{t.answer.answer}</p>
        </div>
      ) : <p className="text-ink-2">No answer — sent to the event’s managers.</p>}
      <div className="flex flex-wrap items-center gap-2">
        {t.answer && <Button className="max-sm:min-h-11" onClick={copy} data-testid={id('ask-copy')}>{copied ? 'Copied' : 'Copy'}</Button>}
        {t.sent === 'sent' ? (
          <Badge tone="success" data-testid={id('ask-sent')}>Sent</Badge>
        ) : (
          <Button className="max-sm:min-h-11" busy={t.sent === 'sending'} busyLabel="Sending…" onClick={onSend} data-testid={id('ask-send')}>Send to admin</Button>
        )}
      </div>
      {t.error && (
        <div className="flex flex-wrap items-center gap-2">
          <p role="alert" className="text-st-danger">{t.error}</p>
          <InlineReport message={t.error} />
        </div>
      )}
    </article>
  );
}
